/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// v1_862: ONE HOLDING LINE, READ AS HOLDER, PERCENTAGE AND HELD COMPANY.
// A signed group structure chart was read one line per holder, the whole line
// kept as the holder's name ("Ivan Morozski — 100% of Harbour Quay Ltd (UK,
// Company No. 15531907)"). The line contains "Ltd", so the holder read as a
// company, and the natural persons only step left no beneficial owner (Next
// Markets, production 28/09/26). Every line was also recorded as a holding of
// the applicant, including the person's other companies; the register pass
// split the line at the comma inside its brackets; and a PSC holder marked
// "ceased" read as current. This module reads the line itself:
//   parseHoldingLine: holder, percentage and held company, brackets respected;
//   holdingStatus: current, or ceased with the date the document gives;
//   sameCompany: the applicant test on company names (suffixes ignored).

const clean = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();

// Company identity for the applicant test: case, punctuation and legal form
// suffixes ignored ("Harbour Quay Ltd" is "HARBOUR QUAY LIMITED").
const SUFFIX = /\b(limited|ltd|plc|llp|llc|inc|incorporated|corp|corporation|gmbh|ag|sa|sarl|bv|nv|oy|ab|aps|pte|pty|s\.?r\.?o|a\.?s|sp\.? z o\.?o)\b\.?/g;
export function companyKey(v) {
  return entityName(v).toLowerCase().replace(/\([^)]*\)/g, ' ').replace(/[.,]/g, ' ').replace(SUFFIX, ' ').replace(/[^a-z0-9&À-ɏ ]/g, ' ').replace(/\s+/g, ' ').trim();
}
export function sameCompany(a, b) {
  const x = companyKey(a), y = companyKey(b);
  return !!x && x === y;
}

// Top level text: each bracketed span replaced by spaces of the same length,
// so separators inside brackets ("(UK, Company No. 15531907)") are never seen.
function topLevel(s) {
  let depth = 0, out = '';
  for (const ch of s) {
    if (ch === '(' || ch === '[') { depth++; out += ' '; continue; }
    if ((ch === ')' || ch === ']') && depth > 0) { depth--; out += ' '; continue; }
    out += depth > 0 ? ' ' : ch;
  }
  return out;
}

// v1_867: a series of a series LLC is its own company. "Brackmoor LLC (Series
// 1)" and "Brackmoor LLC Series 1" are the same holder and never the bare
// "Brackmoor LLC" or another series; the designation is part of the name, not
// bracketed detail (Calder Ridge, production 29/09/26, where every series
// read as "Brackmoor LLC"). A jurisdiction or registration bracket stays detail.
const SERIES_IN_BRACKETS = /\(\s*(series\s+[a-z0-9]+)\s*\)/i;
export function entityName(v) {
  return clean(String(v == null ? '' : v).replace(SERIES_IN_BRACKETS, (m0, ser) => ' ' + ser.replace(/^series/i, 'Series')));
}
export function seriesOf(v) {
  const m = entityName(v).match(/\bseries\s+([a-z0-9]+)\b/i);
  return m ? m[1].toUpperCase() : null;
}
// A held company as written, and its bracketed detail ("UK, Company No. ...").
function splitHeld(s) {
  const t = entityName(s);
  const m = t.match(/^(.*?)\s*\(([^()]*)\)\s*$/);
  return m ? { name: clean(m[1]), detail: clean(m[2]) } : { name: t, detail: '' };
}

// Current, or ceased with the date where the document gives one. Ceased ONLY
// from: the status the reader declared; a bracketed marking ("(ceased 24
// October 2023)", "(resigned)", "(former)"); or "ceased" or "resigned"
// followed by a date. A word such as "Former", "removed" or "no longer"
// anywhere else in a line is part of a name or of prose and never makes a
// holder ceased ("Former Estates Ltd, 40% of Harbour Quay Ltd" is current).
const DATE = '([0-9]{1,2}(?:st|nd|rd|th)?\\s+[A-Za-z]+\\s+[0-9]{4}|[A-Za-z]+\\s+[0-9]{1,2},?\\s+[0-9]{4}|[0-9]{4}-[0-9]{2}-[0-9]{2}|[0-9]{1,2}[./][0-9]{1,2}[./][0-9]{2,4})';
const BRACKET_MARK = /\(\s*(ceased|resigned|former)\b([^)]*)\)/i;
const DATED_MARK = new RegExp('\\b(ceased|resigned)\\s+(?:on\\s+)?' + DATE, 'i');
export function holdingStatus(text, declared) {
  const d = clean(declared).toLowerCase();
  const t = clean(text);
  const b = t.match(BRACKET_MARK);
  const w = b ? null : t.match(DATED_MARK);
  if (d === 'ceased' || d === 'resigned' || d === 'former' || b || w) {
    const on = b ? clean(String(b[2] || '').replace(/^\s*(on)?\s*/i, '')) : (w ? clean(w[2]) : '');
    return { status: 'ceased', ceasedOn: on || null };
  }
  return { status: 'current', ceasedOn: null };
}
// The holder's name with a status marking taken off ("Karim Dasti (ceased 24
// October 2023)" is "Karim Dasti"); only the markings holdingStatus reads.
function withoutStatus(name) {
  return clean(String(name).replace(new RegExp(BRACKET_MARK.source, 'ig'), ' ').replace(new RegExp('\\b(ceased|resigned)\\s+(?:on\\s+)?' + DATE, 'ig'), ' '));
}

/**
 * Read one holding line.
 *   "Ivan Morozski — 100% of Harbour Quay Ltd (UK, Company No. 15531907)"
 *   "Ivan Morozski, 100% of Harbour Quay Ltd (UK, Company No. 15531907)"
 *   "Ivan Morozski in Harbour Quay Ltd (UK, Company No. 15531907)"
 *   "PQ SUNRISE HOLDINGS LIMITED — 100% of NovaPulse s.r.o. (Czech Republic, IČO 17264531)"
 *   "Ivan Morozski — NovaPulse s.r.o. and QWRT Capital Pty Ltd via PQ SUNRISE HOLDINGS LIMITED"
 * Returns { holder, percentage, held, heldDetail, heldList, via, parsed,
 * status, ceasedOn }. A plain name (no held company in the line) comes back
 * unparsed with holder = the name, so a register's bare holder is unchanged.
 */
export function parseHoldingLine(text, declaredStatus) {
  const raw = clean(text);
  // A piece of a line cut at a comma inside its brackets ("Company No.
  // 15531907) Ivan Morozski — 100% of Harbour Quay Ltd (UK") is not a holding:
  // it is flagged so callers drop it rather than read a holder out of it.
  let depth = 0, broken = false;
  for (const ch of raw) { if (ch === '(' || ch === '[') depth++; else if (ch === ')' || ch === ']') { depth--; if (depth < 0) broken = true; } }
  if (broken || depth !== 0) return { holder: raw, percentage: null, held: '', heldDetail: '', heldList: null, via: null, parsed: false, fragment: true, status: 'current', ceasedOn: null };
  const st = holdingStatus(raw, declaredStatus);
  const base = withoutStatus(raw);
  const top = topLevel(base);
  const out = (holder, percentage, heldRaw, extra = {}) => {
    const h = heldRaw != null ? splitHeld(heldRaw) : { name: '', detail: '' };
    return { holder: entityName(holder), percentage: percentage != null ? String(percentage) : null, held: h.name, heldDetail: h.detail, heldList: null, via: null, parsed: true, ...st, ...extra };
  };
  // Positions come from the top level text, the pieces from the real text.
  const piece = (a, b) => clean(base.slice(a, b));
  let m;
  // v1_867: "<person> (in <held>, via <intermediate>)": an indirect holding
  // of <held> through <intermediate>, the person the holder (a group chart's
  // indirect line, which read whole as a company because it names "Inc").
  if ((m = base.match(/^([^()]+?)\s*\(\s*in\s+(.+?),?\s+via\s+([^()]+?)\s*\)\s*$/i))) {
    return { ...out(m[1], null, m[2]), via: clean(m[3]), basis: 'indirect' };
  }
  // v1_867: "<holder> — Certificate No. 001, 1,000 Common shares ...": a
  // share ledger line; the holder is the name before the certificate or the
  // share count, the entity held is the ledger's subject.
  if ((m = top.match(/^(.+?)\s*(?:[—–]|\s-\s|,)\s*(?:certificate\b|cert\.?\s*no\b|share certificate\b|[0-9][0-9,.]*\s+(?:ordinary|common|preference|preferred|class\s+\w+)?\s*shares\b)/i))) {
    return { ...out(splitHeld(piece(0, m[1].length)).name, null, null), held: '', shareLedger: true };
  }
  // v1_867: "<person> — <company>" or "<person>, <company>" with no
  // percentage and no "of" or "in": the person is a member of that company
  // (an LLC's schedule of members lists "Aaron Vane — Brackmoor LLC Series
  // 1"). Read whole, the line became a company holding the applicant.
  if ((m = top.match(/^(.+?)\s*(?:[—–]|\s-\s|,)\s*(.+)$/)) && !/\d\s*%|\b(of|in|via|holds|owns)\b/i.test(m[2])) {
    const who = piece(0, m[1].length), what = entityName(base.slice(top.indexOf(m[2], m[1].length)));
    if (looksLikePersonName(who) === true && (CORP_WORD.test(what) || seriesOf(what))) return out(who, null, what);
  }
  // "<holder> — <list> via <intermediate>": an indirect summary of other
  // companies, never a holding of the applicant in itself.
  if ((m = top.match(/^(.+?)\s+[—–-]\s+(.+?)\s+via\s+(.+)$/i)) && !/\d\s*%/.test(m[2])) {
    const i1 = m[1].length, iList = top.indexOf(m[2], i1), iVia = top.lastIndexOf(' via ');
    const list = piece(iList, iVia).split(/\s+and\s+|,\s*/i).map(s => splitHeld(s).name).filter(Boolean);
    return { ...out(piece(0, i1), null, null), heldList: list, via: clean(base.slice(iVia + 5)) };
  }
  // "<holder> — 100% of <held>" and "<holder>, 100% of <held>" and
  // "<holder>: 100% of <held>".
  if ((m = top.match(/^(.+?)\s*(?:[—–:]|\s-\s|,)\s*(\d+(?:\.\d+)?)\s*%\s*(?:of|in)\s+(.+)$/i))) {
    const i1 = m[1].length, iHeld = top.lastIndexOf(m[3]);
    return out(piece(0, i1), m[2], base.slice(iHeld));
  }
  // "<holder> holds|owns 100% of <held>".
  if ((m = top.match(/^(.+?)\s+(?:holds|owns)\s+(?:(\d+(?:\.\d+)?)\s*%\s*)?(?:of\s+)?(.+)$/i))) {
    const i1 = m[1].length, iHeld = top.lastIndexOf(m[3]);
    return out(piece(0, i1), m[2] != null ? m[2] : null, base.slice(iHeld));
  }
  // "<holder> in <held>" (the percentage is stated beside the line).
  if ((m = top.match(/^(.+?)\s+in\s+(.+)$/i))) {
    // The space right before "in", so a bracket ending the holder's name
    // ("Brackmoor LLC (Series 1) in ...") stays with the holder.
    const iIn = top.search(/\sin\s/i), iHeld = top.indexOf(m[2], iIn);
    return out(splitHeld(piece(0, iIn)).name, null, base.slice(iHeld));
  }
  return { holder: base, percentage: null, held: '', heldDetail: '', heldList: null, via: null, parsed: false, ...st };
}

// v1_867: the share a holding states. A stated percentage anywhere in the
// text wins ("1,000 shares (sole shareholder of record, 100% of shares
// issued)" is 100, never the 1 of "1,000"); a count of shares with no
// percentage states none. A membership stated as voting and non voting
// interests ("100% voting interest, 0% non-voting interest") is a voting
// interest, recorded as votingPercentage, not an equity share: the register of
// members states the share (Calder Ridge, where the operating agreement's 100
// percent vote would otherwise stand for the register's 96 percent holding).
export function statedShare(text) {
  const t = clean(text);
  if (!t) return { percentage: '', voting: null };
  if (/\bvoting\s+interest/i.test(t) && /\bnon[\s-]?voting\b/i.test(t)) {
    const v = t.match(/(\d+(?:\.\d+)?)\s*%\s*voting/i);
    return { percentage: '', voting: v ? v[1] : null };
  }
  const PCT = /(\d+(?:\.\d+)?)\s*(?:%|per\s*cent\b|percent\b)/i;
  // v1_867: a line stating the equity and the voting rights apart ("10% of the
  // shares, 60% of the voting rights") keeps both: the votes as voting, the
  // equity as the share. Votes count for the beneficial owner test on their own.
  const VOTE = /(\d+(?:\.\d+)?)\s*(?:%|per\s*cent\b|percent\b)\s*(?:of\s+(?:the\s+)?)?(?:voting\s+rights?|votes?\b)/i;
  const vm = t.match(VOTE);
  if (vm) {
    const rest = clean(t.replace(vm[0], ' '));
    const em = rest.match(PCT);
    return { percentage: em ? em[1] + '%' : '', voting: vm[1] };
  }
  const lead = t.replace(/\s*\(.*$/, '');
  // The text as written where its own words state the share ("75 percent or
  // more", a band every reader keeps); the bracketed figure where only the
  // bracket does ("1,000 shares (100% of shares issued)").
  if (PCT.test(lead)) return { percentage: t, voting: null };
  const inBracket = t.match(PCT);
  if (inBracket) return { percentage: inBracket[1] + '%', voting: null };
  if (/\bshares?\b/i.test(t) && /\d/.test(t)) return { percentage: '', voting: null };
  return { percentage: t, voting: null };
}

// Is the parsed holder a natural person's name? true, false, or null where it
// cannot tell (the caller then takes the type the reader declared).
//   false: a company form or institutional word, or digits;
//   true: two to eight words and none of those ("Abdullah bin Khalid bin
//     Faisal Al Saud" is a person);
//   null: one word, or more than eight.
const CORP_WORD = /\b(ltd|limited|plc|llp|llc|inc|corp|corporation|company|holdings?|group|gmbh|ag|sa|sarl|bv|nv|oy|ab|aps|se|kg|ug|pte|pty|s\.?r\.?o|trust|foundation|nominees?|partners?|capital|investments?|lab|labs|fund|bank)\b/i;
const INSTITUTION_WORD = /\b(register|registrar|registered|held by|trustees?|pension|scheme|fund|bank|securities|nominee|custodian|depositar|treasury|employee|share plan|esop|equiniti|computershare|associates|ventures|management|services|international|worldwide|global|enterprises?|industries|properties|estates|council|authority|government|ministry|state of|republic)\b/i;
export function looksLikePersonName(name) {
  const n = clean(name);
  if (!n) return false;
  if (/\d/.test(n) || CORP_WORD.test(n) || INSTITUTION_WORD.test(n)) return false;
  const words = n.split(/\s+/).filter(Boolean);
  if (words.length >= 2 && words.length <= 8) return true;
  return null;
}

export default parseHoldingLine;

// v1_877: a stated share for display, in words and without dashes. A bare
// figure reads "N percent"; a figure the text already states keeps its own
// words ("75% or more" reads "75 percent or more"), so the panel never prints
// "75% or more percent". A Companies House band line ("Ownership of shares,
// 75% or more; ownership of voting rights, 75% or more") reads by its first
// figure. Pure.
export function shareInWords(text) {
  const t = String(text == null ? '' : text).trim();
  if (!t) return '';
  if (/^\d+(?:\.\d+)?%?$/.test(t)) return t.replace(/%$/, '') + ' percent';
  const between = t.match(/more than\s+(\d+(?:\.\d+)?)\s*%[^\d]{0,40}?(?:not more than|up to)\s+(\d+(?:\.\d+)?)\s*%/i);
  if (between) return 'more than ' + between[1] + ' and up to ' + between[2] + ' percent';
  const band = t.match(/(\d+(?:\.\d+)?)\s*%?\s*(?:-|\u2013|\u2014|to)\s*(\d+(?:\.\d+)?)\s*(?:%|per\s*cent\b|percent\b)/i);
  if (band) return band[1] + ' to ' + band[2] + ' percent';
  const m = t.match(/(\d+(?:\.\d+)?)\s*(?:%|per\s*cent\b|percent\b)\s*(or more|or less|\+)?/i);
  if (!m) return t.replace(/\s*[\u2013\u2014]\s*/g, ', ');
  return m[1] + ' percent' + (m[2] ? (m[2] === '+' ? ' or more' : ' ' + m[2].toLowerCase()) : '');
}
